/* eslint-disable no-restricted-syntax */

import './fonts/fonts.css';
import './i18n';
// Eagerly import all API clients to ensure they're registered before lazy routes load
import '@/client/services/api/clients/analyzeApiClient';
import '@/client/services/api/clients/graphqlClient';
import '@/client/services/api/clients/hubApiClient';
import '@/client/services/api/clients/learnApiClient';
import '@/client/services/api/clients/learnV4ApiClient';
import '@/client/services/api/clients/openAIClient';
import '@/client/services/api/clients/preAuthApiClient';
import '@/client/services/api/clients/reportsApiClient';
import '@/client/services/api/clients/v3ApiClient';

import { Box, ChakraProvider, extendTheme, useToast } from '@chakra-ui/react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';

import AppAnnouncementBanner from '@/client/components/shared/messaging/AppAnnouncementBanner';
import CookieConsentBanner from '@/client/components/shared/consent/CookieConsentBanner';
import ConfigService from '@/client/services/api/ConfigService';
import Loading from '@/client/components/media/Loading';
import { QueryParamProvider } from 'use-query-params';
import {
  forgetDeferredCampaign,
  initCustomerGA,
  trackPageView,
  updateCustomerGAConsent,
} from '@/client/utils/AnalyticsProvider';
import { useConsentStore } from '@/client/services/state/consentStore';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import { api } from 'api';
import customTheme from './theme/theme';
import { useAuthStore } from '@/client/services/state/authStore';
import { useConfigStore } from '@/client/services/state/configStore';
import { useCompanyConfig } from '@/client/services/hooks/auth/useCompanyConfig';
import { useCustomFavicon } from './services/hooks/media/useCustomFavicon';
import { useIntercom } from './services/hooks/useIntercom';
import { useNPS } from './services/hooks/useNPS';
import { usePartnerStore } from './services/state/partnerStore';
import { useQuery } from '@tanstack/react-query';
import { useToastStore } from '@/client/services/state/toastStore';
import { useTranslation } from 'react-i18next';

function App() {
  useCustomFavicon();
  const navigate = useNavigate();
  const location = useLocation();
  const chakraToast = useToast();
  const { config, setPublicConfig } = useConfigStore();
  const { toast, setToast } = useToastStore();
  const [theme, setTheme] = useState<any>();
  const [hostname, setHostname] = useState<string>('');
  const { authConfig } = useAuthStore();
  const { partner } = usePartnerStore();
  const { i18n } = useTranslation();
  const { bootIntercomUser, intercomUpdate, intercomShutdown } = useIntercom();
  const { addNPS, removeNPS } = useNPS();
  const analyticsConsent = useConsentStore((s) => s.categories.analytics);
  const functionalConsent = useConsentStore((s) => s.categories.functional);

  const updateThemeFromEffect = () => {
    if (!config.theme || Object.keys(config.theme).length === 0) {
      return;
    }
    let partnerPrimary: string | null = partner?.branding?.color_primary;
    if (partnerPrimary === '') {
      partnerPrimary = null;
    }
    const newTheme = extendTheme(customTheme, {
      colors: {
        brand: {
          primary: partnerPrimary ?? config.theme.colors.brand.primary,
          secondary: config.theme.colors.brand.secondary,
          tertiary: config.theme.colors.brand.tertiary,
          button:
            partnerPrimary ??
            (config.theme.colors.overrides?.buttons !== ''
              ? config.theme.colors.overrides?.buttons
              : config.theme.colors.brand.primary),
          link:
            partnerPrimary ??
            (config.theme.colors.overrides?.links !== ''
              ? config.theme.colors.overrides?.links
              : config.theme.colors.brand.primary),
          tableHeader:
            partnerPrimary ??
            (config.theme.colors.overrides?.headers !== ''
              ? config.theme.colors.overrides?.headers
              : config.theme.colors.brand.primary),
          formInput:
            partnerPrimary ??
            (config.theme.colors.overrides?.formInputs !== ''
              ? config.theme.colors.overrides?.formInputs
              : config.theme.colors.brand.primary),
          highlight:
            partnerPrimary ??
            (config.theme.colors.overrides?.highLights !== ''
              ? config.theme.colors.overrides?.highLights
              : config.theme.colors.brand.primary),
        },
      },
    });
    setTheme(newTheme);
  };

  useEffect(() => {
    console.log(api());

    window.location.hostname === 'local.continu.co'
      ? setHostname('staging.continu.co')
      : setHostname(window.location.hostname);
  }, []);

  useEffect(() => {
    trackPageView(location.pathname);

    if (authConfig.authtoken) {
      intercomUpdate();
    }
  }, [location.pathname]);

  // Boot the customer's GA property once auth + analytics consent are both
  // present; runs on refresh/SSO entry too, not just interactive login.
  // Re-runs on /me refetches (id changes), consent toggles (revoke/regrant) and
  // navigations (initCustomerGA waits for a URL whose query and hash are safe for gtag.js).
  useEffect(() => {
    if (authConfig.authtoken && analyticsConsent) {
      initCustomerGA(authConfig.company?.google_analytics);
    } else {
      forgetDeferredCampaign();
    }
    updateCustomerGAConsent(analyticsConsent);
  }, [authConfig, analyticsConsent, location.key]);

  // Boot Intercom/NPS from auth state, not from the login mutation, so every
  // session entry (password, SAML, hub transition, page refresh) and a
  // post-login consent grant all boot; a consent revoke shuts Intercom down.
  useEffect(() => {
    if (!authConfig.authtoken) {
      intercomShutdown();
      removeNPS();

      return;
    }

    if (functionalConsent) {
      bootIntercomUser(config, authConfig);
    } else {
      intercomShutdown();
    }

    if (analyticsConsent) {
      addNPS(authConfig);
    } else {
      removeNPS();
    }
    // reason: the four hook callbacks are fresh closures every render, so
    // listing them would re-fire this effect (and the boot) on every render.
  }, [authConfig, functionalConsent, analyticsConsent, config.intercomAppId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (authConfig?.user?.language) {
      i18n.changeLanguage(authConfig.user.language);
    }
  }, [authConfig]);

  useEffect(() => {
    updateThemeFromEffect();
  }, [partner, config.theme]);

  useEffect(() => {
    if (toast.show) {
      chakraToast({
        description: toast.title,
        status: toast.status,
        duration: 5000,
        isClosable: true,
        position: 'top',
        onCloseComplete: () => setToast({ show: false, status: 'success', title: '' }),
      });
    }
  }, [toast]);

  const { isLoading: isConfigLoading, isError: isConfigError } = useQuery({
    queryKey: ['config', hostname],
    queryFn: () => ConfigService.getConfig(hostname),
    refetchOnWindowFocus: false,
    enabled: hostname !== '',
    onSuccess: (res: any) => {
      setPublicConfig(res);

      if (res.customCopy) {
        const { customCopy } = res;

        for (const langKey of Object.keys(customCopy)) {
          const customCopyObj: { [key: string]: string } = {};

          for (const transPair of customCopy[langKey]) {
            customCopyObj[transPair.key] = transPair.value;
          }

          i18n.addResourceBundle(langKey, 'translation', customCopyObj, true, true);
        }
      }
    },
    onError: (err: any) => {
      console.log('Error', err.response?.data || err);
      navigate('/');
      setTheme(customTheme);
    },
  });

  // Everything past the login screen reads the signed-in config (hub links, global group ids,
  // feature flags), so hold rendering until it has merged in.
  const { isAwaitingCompanyConfig, isCompanyConfigUnavailable } = useCompanyConfig();

  if (isConfigLoading || isAwaitingCompanyConfig) {
    return <Loading />;
  }

  if (isConfigError || isCompanyConfigUnavailable) {
    throw new Error('Unable to fetch application configuration, please try again later.');
  }

  return (
    <QueryParamProvider adapter={ReactRouter6Adapter}>
      <ChakraProvider theme={theme}>
        <Box>
          <CookieConsentBanner />
          <AppAnnouncementBanner />
          <Outlet />
        </Box>
      </ChakraProvider>
    </QueryParamProvider>
  );
}

export default App;
